/**
 * Structure-sharing merge of live territory state onto static map geometry.
 *
 * Every socket message writes a brand-new GameResponse into the query cache, and
 * a bot's turn produces a burst of them. Rebuilding the hydrated state from
 * scratch each time handed MapView a new `state`, a new `state.map` and a new
 * array of EVERY territory — so the whole board re-rendered because one army
 * moved, or because a TURN_CHANGED touched no territory at all.
 *
 * Measured on an iPhone 16 Pro simulator before this existed: 100% of MapView
 * renders were attributed to `state` + `state.territories` changing identity, at
 * ~2.3 full-board rebuilds per second through a bot's turn, and 22-62 renders
 * just to open a game. After: 4-5 renders, 2 of them from state.
 *
 * So reuse what did not change:
 *   • a territory keeps its identity while `ownerId` and `armies` are unchanged
 *   • the territories ARRAY is reused when no territory changed at all
 *   • the hydrated `map` is reused when its territories array is reused
 *
 * Downstream that turns "the whole board changed" into "these two territories
 * changed": the geometric layers in MapView key off `state.map.territories`, so
 * they keep their element references and React skips those subtrees entirely.
 *
 * Identity is the entire point, which is why the cache is threaded through
 * explicitly rather than hidden in a closure — it makes the behaviour testable
 * without a renderer (see tests/hydrateTerritories.test.ts).
 */
import type { MapDef, WireTerritory } from '@warcarp/api-types'

type Territory = MapDef['territories'][number]

/**
 * Live per-territory fields as they arrive on the wire. In fog-of-war games a
 * territory outside the viewer's vision has no armies (and, in heavy fog, no
 * owner) and is flagged `fogged`.
 */
export type SlimTerritory = WireTerritory

export type HydrateCache = {
  mapDef: MapDef | null
  byId: Map<string, Territory>
  territories: Territory[]
  map: MapDef | null
}

export function createHydrateCache(): HydrateCache {
  return { mapDef: null, byId: new Map(), territories: [], map: null }
}

/**
 * Merge `slim` onto `mapDef`'s geometry, reusing objects from `cache` wherever
 * nothing changed. Mutates `cache` — one cache per mounted consumer.
 */
export function hydrateMap(
  cache: HydrateCache,
  mapDef: MapDef,
  slim: ReadonlyArray<SlimTerritory> | undefined,
): MapDef {
  // A different map invalidates every cached object.
  if (cache.mapDef !== mapDef) {
    cache.mapDef = mapDef
    cache.byId = new Map()
    cache.territories = []
    cache.map = null
  }

  let territories: Territory[]
  if (slim && slim.length > 0) {
    const stateById = new Map(slim.map((t) => [t.id, t]))
    let changed = cache.territories.length !== mapDef.territories.length
    const next = mapDef.territories.map((t) => {
      const dynamic = stateById.get(t.id)
      if (!dynamic) {
        // No live row for this territory — the static object IS the answer.
        if (cache.byId.get(t.id) !== t) changed = true
        cache.byId.set(t.id, t)
        return t
      }
      // Fogged rows hydrate to an explicit flag, never undefined counts:
      // armies 0 (meaningless; readers check `fogged`), owner '' in heavy fog.
      const fogged = dynamic.fogged === true
      const ownerId = dynamic.ownerId ?? ''
      const armies = fogged ? 0 : (dynamic.armies ?? 0)
      const prev = cache.byId.get(t.id)
      if (prev && prev.ownerId === ownerId && prev.armies === armies && (prev.fogged ?? false) === fogged) {
        return prev
      }
      changed = true
      const merged = fogged ? { ...t, ownerId, armies, fogged: true } : { ...t, ownerId, armies }
      cache.byId.set(t.id, merged)
      return merged
    })
    territories = changed ? next : cache.territories
    if (changed) cache.territories = next
  } else {
    // No slim territories yet (LOBBY, or a very old row) — static geometry as-is.
    territories = mapDef.territories
  }

  const map =
    cache.map && cache.map.territories === territories ? cache.map : { ...mapDef, territories }
  cache.map = map
  return map
}
